import { useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, Info } from "lucide-react";
import { EnergySuccessIllustration } from "./EnergySuccessIllustration";

type ResultCopy = {
  eyebrow?: string;
  title?: string;
  description?: string;
  submitCta?: string;
  restartCta?: string;
};

function formatPrice(value: unknown) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "Price on request";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(numberValue);
}

function MetricGrid({ metrics = [] }: { metrics?: any[] }) {
  if (!Array.isArray(metrics) || metrics.length === 0) return null;

  return (
    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {metrics.map((metric) => (
        <div
          key={`${metric.label}-${metric.value}`}
          className="rounded-xl border border-[var(--line)] bg-white/80 px-3 py-2"
        >
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
            <span>{metric.label}</span>
            {metric.info && (
              <span className="group relative inline-flex" title={metric.info}>
                <Info size={13} className="text-[var(--brand)]" />
                <span className="pointer-events-none absolute left-0 top-5 z-50 hidden w-64 max-w-[calc(100vw-4rem)] rounded-xl border border-[var(--line)] bg-white p-3 text-left text-xs normal-case leading-relaxed tracking-normal text-[var(--text-primary)] shadow-xl group-hover:block sm:left-1/2 sm:-translate-x-1/2">
                  {metric.info}
                </span>
              </span>
            )}
          </div>
          <p className="mt-1 text-sm font-semibold text-[var(--text-primary)]">
            {metric.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function getDetailMetrics(metrics: any[] = []) {
  if (!Array.isArray(metrics)) return [];

  return metrics.filter((metric) => {
    const label = String(metric?.label ?? "").toLowerCase();
    return !label.includes("efficiency improvement") && !label.includes("annual savings");
  });
}

function EnergySavingsPanel({ savings }: { savings?: any }) {
  if (!savings) return null;

  return (
    <section className="overflow-hidden rounded-[1.35rem] border border-lime-200 bg-[linear-gradient(110deg,#f7fee7_0%,#efffd1_52%,#fff7ed_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
      <div className="p-4 sm:p-5">
        <p className="text-xs font-black uppercase tracking-[0.16em] text-lime-700">
          Estimated annual savings*
        </p>

        <div className="mt-3 grid grid-cols-2 items-end gap-4">
          <div className="min-w-0">
            <div className="inline-flex items-end gap-2">
              <span className="text-[2rem] font-black leading-none tracking-tight text-lime-700 sm:text-[2.5rem]">
                {formatPrice(savings.annualSavings)}
              </span>
              <span className="pb-1 text-xs font-bold leading-none text-lime-700">Per Year</span>
            </div>
          </div>

          <div className="min-w-0 text-right">
            <div className="inline-flex items-end justify-end gap-2">
              <span className="text-[2rem] font-black leading-none tracking-tight text-lime-700 sm:text-[2.5rem]">
                {savings.percentSavings}%
              </span>
              <span className="w-20 pb-1 text-left text-xs font-bold leading-[1.05] text-lime-700">
                Lower Yearly AC Bill
              </span>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}

function SavingsFootnote({ options = [] }: { options?: any[] }) {
  const hasSavings = options.some((option) => !!option?.energySavings);
  if (!hasSavings) return null;

  const tonnage = options.find((option) => option?.energySavings?.tonnage)?.energySavings?.tonnage;

  return (
    <p className="px-1 text-[11px] leading-relaxed text-[var(--text-muted)]">
      *Estimated savings are based on {tonnage || "matched"} air conditioner data. Actual savings may vary based on
      usage, weather, utility rates, and final equipment selection.
    </p>
  );
}

export function Result({ options = [], onSubmit, onRestart, copy = {} }: any & { copy?: ResultCopy }) {
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [expandedOptionId, setExpandedOptionId] = useState<string | null>(null);
  const [sentState, setSentState] = useState<"idle" | "sending" | "error">(
    "idle",
  );

  const selectedOption = useMemo(() => {
    if (!selectedOptionId) return null;
    return options.find((option: any, idx: number) => `${option.title}-${idx}` === selectedOptionId);
  }, [options, selectedOptionId]);

  return (
    <div className="space-y-5 rounded-3xl border border-[var(--line)] bg-white p-5 shadow-sm sm:p-6">
      <div className="flex justify-center">
        <EnergySuccessIllustration className="h-24 w-auto sm:h-28" />
      </div>

      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--brand)]">
          {copy.eyebrow ?? "Your Estimate Is Ready"}
        </p>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          {copy.title ?? "Pick the option that fits your home"}
        </h2>
        <p className="text-sm text-[var(--text-muted)]">
          {copy.description ?? "You can choose the best fit for your budget and needs."}
        </p>
      </div>

      {options.length > 0 ? (
        <div className="space-y-2">
          {options.map((option: any, idx: number) => {
            const optionId = `${option.title}-${idx}`;
            const isSelected = selectedOptionId === optionId;
            const isExpanded = expandedOptionId === optionId;
            const startingPrice = option.startingPrice ?? option.rangeMin ?? option.price;
            const startingMonthlyPrice =
              option.startingMonthlyPrice ?? option.monthlyPrice ?? null;
            const detailMetrics = getDetailMetrics(option.metrics);
            const hasExtraDetails =
              detailMetrics.length > 0 ||
              !!option.headline ||
              !!option.description ||
              (Array.isArray(option.highlights) && option.highlights.length > 0);

            return (
              <div
                key={optionId}
                className={`
                  relative w-full rounded-2xl border p-4 text-left transition
                  ${
                    isSelected
                      ? "border-[var(--brand)] bg-white shadow-[0_0_0_4px_rgba(248,57,36,0.14),0_18px_45px_rgba(15,23,42,0.10)]"
                      : "border-[var(--line)] bg-slate-50 hover:bg-slate-100"
                  }
                `}
              >
                {isSelected && (
                  <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                    <CheckCircle2 size={13} strokeWidth={2.5} />
                    Selected option
                  </div>
                )}

                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => {
                    setSelectedOptionId(optionId);
                    setExpandedOptionId((current) => (current === optionId ? current : null));
                  }}
                  aria-pressed={isSelected}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex min-w-0 flex-wrap items-center gap-2">
                        <p className="font-semibold text-[var(--text-primary)]">{option.title}</p>
                        <span className="rounded-full bg-[var(--brand-soft)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--brand)]">
                          {option.tag}
                        </span>
                      </div>

                      <p className="mt-1 text-sm font-semibold text-[var(--text-primary)]">
                        Starting at{" "}
                        <span className="text-xl font-bold text-[var(--brand)]">
                          {formatPrice(startingPrice)}
                        </span>
                        {startingMonthlyPrice ? (
                          <span className="text-[var(--text-muted)]">
                            {" "}or {formatPrice(startingMonthlyPrice)}/mo
                          </span>
                        ) : null}
                      </p>
                    </div>
                    {option.imageUrl && (
                      <div className="relative flex-none">
                        {isSelected && (
                          <span className="absolute -right-1 -top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-sm ring-2 ring-white">
                            <CheckCircle2 size={15} strokeWidth={2.7} />
                          </span>
                        )}
                        <img
                          src={option.imageUrl}
                          alt={`${option.title} HVAC product`}
                          className="h-20 w-20 object-contain sm:h-24 sm:w-24"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>
                </button>

                <EnergySavingsPanel savings={option.energySavings} />

                {hasExtraDetails && (
                  <button
                    type="button"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full px-1 text-xs font-semibold text-[var(--brand)] transition hover:text-[var(--brand-dark)]"
                    onClick={() =>
                      setExpandedOptionId((current) => (current === optionId ? null : optionId))
                    }
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? "Hide details" : "See details"}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-300 ease-out ${
                        isExpanded ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>
                )}

                <div
                  className={`grid transition-[grid-template-rows,opacity,margin-top] duration-300 ease-out ${
                    isExpanded
                      ? "mt-3 grid-rows-[1fr] opacity-100"
                      : "mt-0 grid-rows-[0fr] opacity-0"
                  }`}
                  aria-hidden={!isExpanded}
                >
                  <div className={isExpanded ? "overflow-visible" : "overflow-hidden"}>
                    <div
                      className={`space-y-2 border-t border-[var(--line)] pt-3 transition-transform duration-300 ease-out ${
                        isExpanded ? "translate-y-0" : "-translate-y-1"
                      }`}
                    >
                      <MetricGrid metrics={detailMetrics} />
                      {option.headline && (
                        <p className="text-sm font-medium text-[var(--text-primary)]">{option.headline}</p>
                      )}
                      {option.description && (
                        <p className="text-xs leading-relaxed text-[var(--text-muted)]">
                          {option.description}
                        </p>
                      )}
                      {Array.isArray(option.highlights) && option.highlights.length > 0 && (
                        <ul className="space-y-1 text-xs text-[var(--text-muted)]">
                          {option.highlights.map((item: string) => (
                            <li key={item}>• {item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <SavingsFootnote options={options} />
        </div>
      ) : (
        <p className="rounded-xl border border-[var(--line)] bg-slate-50 px-4 py-3 text-sm text-[var(--text-muted)]">
          We are preparing your best options. A specialist can finalize your estimate.
        </p>
      )}

      {sentState === "error" && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          We could not send your estimate right now. Please try again.
        </p>
      )}

      <button
        className={`
          w-full rounded-xl py-3 text-sm font-semibold text-white transition
          ${
            !selectedOption || sentState === "sending"
              ? "cursor-not-allowed bg-gray-300"
              : "bg-[var(--brand)] hover:bg-[var(--brand-dark)]"
          }
        `}
        disabled={!selectedOption || sentState === "sending"}
        onClick={async () => {
          if (!selectedOption) return;
          try {
            setSentState("sending");
            await onSubmit(selectedOption);
          } catch {
            setSentState("error");
          }
        }}
      >
        {sentState === "sending" ? "Sending..." : copy.submitCta ?? "Finalize estimate"}
      </button>

      <button
        className="w-full rounded-xl border border-[var(--line)] bg-white py-3 text-sm font-semibold text-[var(--text-muted)] transition hover:bg-gray-50"
        onClick={onRestart}
      >
        {copy.restartCta ?? "Start another estimate"}
      </button>
    </div>
  );
}
